/**
 * DIME cloud web-api client.
 *
 * Auth model: every backend call presents a DIME JWT minted through the
 * host-supplied `DimeTokenExchange` (Clerk today; see `identity.ts`). The DIME
 * JWT has a 5-minute TTL and there is no refresh endpoint, so each call here
 * mints a fresh one immediately before the request (and re-mints + retries
 * exactly once on a 401). Nothing returned by this module may be persisted —
 * connection grants are memory-only by contract (`Cache-Control: no-store`).
 *
 * Never log the access code, the DIME JWT, or the grant token.
 */
import {
  decodeJwtPayload,
  DimeApiError,
  type DimeIdentity,
  type DimeTokenExchange,
} from './identity.js';
import { resolveDimeApiUrl, type DimeEnvironment } from './environments.js';

/**
 * Sidecar forward auth for `TerminalConfig.sidecar`: routes the model-catalog
 * and session-sync calls through the web-api's allowlisted `/v1/gateway/*`
 * forward, authenticated by a fresh-enough DIME JWT — the backend injects
 * the real gateway credential, so the browser never needs the gateway URL or
 * token for sidecar calls.
 */
export interface SidecarAuth {
  readonly baseUrl: string;
  readonly getAuthHeaders: () => Readonly<Record<string, string>> | null;
}

/**
 * The in-memory connection grant: everything the terminal needs to reach the
 * user's gateway. `token` is the grant's gateway token when present, otherwise
 * the DIME JWT that resolved the connection (per the /v1/connection contract).
 */
export interface ConnectionGrant {
  readonly gatewayUrl: string;
  readonly token: string;
  /**
   * True when `token` is the DIME JWT itself (gateway-proxy grant with no
   * gateway token). Such a token expires in 5 minutes, so the terminal must
   * mint a fresh one per handshake (`resolveHandshakeToken`) instead of
   * replaying this one on reconnect. False ⇒ `token` is a long-lived gateway
   * credential and must be presented as-is.
   */
  readonly usesDimeJwt: boolean;
}

export type ConnectionResolution =
  | { readonly kind: 'connected'; readonly grant: ConnectionGrant }
  /** 202 — gateway still provisioning; poll again in ~2s. */
  | { readonly kind: 'provisioning' }
  /** 404 — JWT said onboarded but the account is gone; show the code screen. */
  | { readonly kind: 'no-account' }
  /** 409 — failed/suspended; terminal error screen. */
  | { readonly kind: 'unavailable'; readonly status: string };

export type RedeemResolution =
  /**
   * 201 created, or 409 account-already-exists (treated as success). Carries
   * the identity that authorized the redeem so the follow-up connection
   * resolve can reuse it instead of re-exchanging.
   */
  | { readonly kind: 'redeemed'; readonly identity: DimeIdentity }
  /** 400/403 — bad code; show inline and let the user retry. */
  | { readonly kind: 'rejected'; readonly message: string };

export interface DimeCloudOptions {
  /** Named backend to target. Default `'production'`. */
  readonly environment?: DimeEnvironment;
  /** Explicit web-api base URL — wins over `environment`. */
  readonly apiUrl?: string;
  /** How this host obtains DIME JWTs (e.g. `createClerkTokenExchange(getToken)`). */
  readonly exchange: DimeTokenExchange;
}

export interface DimeCloudClient {
  /** The resolved web-api base URL this client targets (no trailing slash). */
  readonly apiUrl: string;
  /** Mint a fresh DIME identity via the host's exchange. */
  readonly mintIdentity: () => Promise<DimeIdentity>;
  /**
   * GET /v1/connection — resolve how this user's client connects. Pass a
   * just-minted `identity` to skip re-exchanging for the first attempt.
   */
  readonly fetchConnection: (
    preMinted?: DimeIdentity,
  ) => Promise<ConnectionResolution>;
  /** POST /v1/account — redeem a one-time operator-issued access code. */
  readonly redeemAccessCode: (code: string) => Promise<RedeemResolution>;
  /**
   * One DIME identity cached in memory, re-minted in the background as it
   * nears its 5-minute expiry. `getAuthHeaders` returning null while a mint is
   * in flight is safe: session sync treats it as transient and retries, and
   * the model fetch falls back to the static catalog. The first mint is kicked
   * eagerly so the initial model fetch usually finds a token waiting.
   */
  readonly createSidecarAuth: () => SidecarAuth;
  /**
   * Fresh-per-handshake DIME JWT for `TerminalConfig.resolveHandshakeToken`
   * (gateway-proxy grants only — the proxy verifies the JWT at every connect,
   * and its 5-minute TTL otherwise outlives the first reconnect). Same cache
   * discipline as the sidecar auth: reuse the current token while it has >60s
   * left, single-flight the mint, and resolve null on a mint failure so the
   * terminal falls back to its stored credential (transient, not fatal).
   */
  readonly createHandshakeTokenResolver: () => () => Promise<string | null>;
}

export function createDimeCloudClient(opts: DimeCloudOptions): DimeCloudClient {
  const apiUrl = resolveDimeApiUrl(opts);
  const { exchange } = opts;

  const mintIdentity = (): Promise<DimeIdentity> => exchange(apiUrl);

  /**
   * Call a DIME endpoint with a just-minted DIME JWT; on 401 (JWT rejected —
   * e.g. clock skew ate the 5-minute TTL) re-exchange and retry exactly once.
   * Returns the response plus the identity whose JWT authorized it, so callers
   * that need the JWT as a fallback gateway token get the one that worked.
   *
   * `identity` lets a caller that just minted one (e.g. the onboarding-check
   * exchange) reuse it for the first attempt instead of minting again; the
   * 401 retry always re-mints, so a stale hand-me-down still self-heals.
   */
  const authedFetch = async (
    path: string,
    init: Omit<RequestInit, 'headers'> & {
      readonly headers?: Record<string, string>;
    } = {},
    identity?: DimeIdentity,
  ): Promise<{ readonly res: Response; readonly identity: DimeIdentity }> => {
    const attempt = async (
      preMinted?: DimeIdentity,
    ): Promise<{
      res: Response;
      identity: DimeIdentity;
    }> => {
      const id = preMinted ?? (await mintIdentity());
      const res = await fetch(`${apiUrl}${path}`, {
        ...init,
        headers: {
          ...init.headers,
          Authorization: `Bearer ${id.token}`,
        },
      });
      return { res, identity: id };
    };

    const first = await attempt(identity);
    if (first.res.status !== 401) return first;
    return attempt();
  };

  const fetchConnection = async (
    preMinted?: DimeIdentity,
  ): Promise<ConnectionResolution> => {
    const { res, identity } = await authedFetch(
      '/v1/connection',
      {},
      preMinted,
    );
    if (res.status === 200) {
      const body = (await res.json()) as { url?: string; token?: string };
      if (typeof body.url !== 'string' || body.url.length === 0) {
        throw new DimeApiError(
          200,
          'Connection grant is missing a gateway URL',
        );
      }
      // Contract: when the grant carries no gateway token, the client
      // presents the DIME JWT itself to the gateway.
      const hasGatewayToken =
        typeof body.token === 'string' && body.token.length > 0;
      return {
        kind: 'connected',
        grant: {
          gatewayUrl: body.url,
          token: hasGatewayToken ? (body.token as string) : identity.token,
          usesDimeJwt: !hasGatewayToken,
        },
      };
    }
    if (res.status === 202) return { kind: 'provisioning' };
    if (res.status === 404) return { kind: 'no-account' };
    if (res.status === 409) {
      const body = (await res.json().catch(() => null)) as {
        status?: string;
      } | null;
      return { kind: 'unavailable', status: body?.status ?? 'failed' };
    }
    throw new DimeApiError(
      res.status,
      `Connection resolve failed (${res.status})`,
    );
  };

  const redeemAccessCode = async (code: string): Promise<RedeemResolution> => {
    const { res, identity } = await authedFetch('/v1/account', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    if (res.status === 201 || res.status === 409) {
      return { kind: 'redeemed', identity };
    }
    if (res.status === 403) {
      return {
        kind: 'rejected',
        message: 'Invalid access code — check it and try again.',
      };
    }
    if (res.status === 400) {
      return {
        kind: 'rejected',
        message: 'That access code can’t be used — check it and try again.',
      };
    }
    throw new DimeApiError(
      res.status,
      `Access-code redeem failed (${res.status})`,
    );
  };

  const createSidecarAuth = (): SidecarAuth => {
    let token: string | null = null;
    let expiresAtMs = 0;
    let minting: Promise<void> | null = null;

    const mint = (): void => {
      if (minting != null) return;
      minting = mintIdentity()
        .then((id) => {
          token = id.token;
          const payload = decodeJwtPayload(id.token);
          const exp = typeof payload?.exp === 'number' ? payload.exp : null;
          expiresAtMs = exp != null ? exp * 1000 : Date.now() + 4 * 60_000;
        })
        .catch(() => {
          // Transient (e.g. host session mid-refresh); the next
          // getAuthHeaders call retries the mint.
        })
        .finally(() => {
          minting = null;
        });
    };
    mint();

    return {
      baseUrl: `${apiUrl}/v1/gateway`,
      getAuthHeaders: () => {
        const current = token;
        // 30s slack so a token never expires mid-request.
        if (current == null || Date.now() >= expiresAtMs - 30_000) {
          mint();
          return null;
        }
        return { Authorization: `Bearer ${current}` };
      },
    };
  };

  const createHandshakeTokenResolver = (): (() => Promise<string | null>) => {
    let token: string | null = null;
    let expiresAtMs = 0;
    let minting: Promise<string | null> | null = null;

    return () => {
      if (token != null && Date.now() < expiresAtMs - 60_000) {
        return Promise.resolve(token);
      }
      minting ??= mintIdentity()
        .then((id) => {
          token = id.token;
          const payload = decodeJwtPayload(id.token);
          const exp = typeof payload?.exp === 'number' ? payload.exp : null;
          expiresAtMs = exp != null ? exp * 1000 : Date.now() + 4 * 60_000;
          return id.token;
        })
        .catch(() => null)
        .finally(() => {
          minting = null;
        });
      return minting;
    };
  };

  return {
    apiUrl,
    mintIdentity,
    fetchConnection,
    redeemAccessCode,
    createSidecarAuth,
    createHandshakeTokenResolver,
  };
}
